import React from 'react';
import { Button } from '../../Button/Button';
import { Icon } from '../../Icon/Icon';
import { Pill, type PillTone } from '../../Pill/Pill';
import { Skeleton } from '../../Skeleton/Skeleton';
import { GeneralInfoSectionCard } from './GeneralInfoSectionCard';

export interface EmailIntegrationAccountRow {
  id: string;
  emailAddress: string;
  provider: string;
  status: string;
  lastSyncAt: string | null;
}

export interface EmailIntegrationSectionProps {
  accounts: EmailIntegrationAccountRow[];
  isLoading?: boolean;
  connecting?: boolean;
  disconnectingId?: string | null;
  onConnectGmail: () => void;
  onDisconnect: (accountId: string) => void;
}

function formatProvider(provider: string): string {
  const value = provider.trim().toLowerCase();
  if (value === 'gmail') return 'Gmail';
  if (value === 'outlook') return 'Outlook';
  return provider || 'Email';
}

function formatLastSync(iso: string | null): string {
  if (!iso) return 'Not synced yet';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface StatusView {
  label: string;
  tone: PillTone;
}

/** Human-readable label + DS tone for a single mailbox's raw API status string. */
function statusView(status: string): StatusView {
  const value = status.trim().toLowerCase();
  if (value === 'active' || value === 'connected') return { label: 'Connected', tone: 'success' };
  if (value === 'pending' || value === 'syncing') return { label: 'Syncing', tone: 'info' };
  if (value === 'error') return { label: 'Needs attention', tone: 'warning' };
  if (value === 'disconnected') return { label: 'Disconnected', tone: 'warning' };
  if (!value) return { label: 'Unknown', tone: 'neutral' };
  return { label: value.charAt(0).toUpperCase() + value.slice(1), tone: 'neutral' };
}

/** A mailbox in this state has stopped syncing and needs the user to re-authorize. */
function needsReconnect(status: string): boolean {
  const value = status.trim().toLowerCase();
  return value === 'error' || value === 'disconnected';
}

/** Rolled-up connection state shown as a Pill in the card header (matches sibling sections). */
function headerStatus(accounts: EmailIntegrationAccountRow[], isLoading: boolean): StatusView | null {
  if (isLoading) return null;
  if (accounts.length === 0) return { label: 'Not connected', tone: 'neutral' };
  if (accounts.some((account) => needsReconnect(account.status))) {
    return { label: 'Needs attention', tone: 'warning' };
  }
  return { label: 'Connected', tone: 'success' };
}

function MailboxRowSkeleton() {
  return (
    <div className="flex items-center gap-x-[var(--sp-3)] px-[var(--sp-4)] py-3.5">
      <Skeleton w={34} h={34} r="var(--r-md)" />
      <div className="flex flex-1 flex-col gap-1.5">
        <Skeleton w={200} h={13} />
        <Skeleton w={128} h={11} />
      </div>
      <Skeleton w={82} h={22} r="var(--r-pill)" />
      <Skeleton w={78} h={30} r="var(--r-pill)" />
    </div>
  );
}

export function EmailIntegrationSection({
  accounts,
  isLoading = false,
  connecting = false,
  disconnectingId = null,
  onConnectGmail,
  onDisconnect,
}: EmailIntegrationSectionProps) {
  const summary = headerStatus(accounts, isLoading);

  return (
    <GeneralInfoSectionCard
      eyebrow="FrontDesk inbox"
      title="Email integration"
      subtitle="Connect your practice Gmail or Outlook mailbox. Inbound mail syncs to the Email inbox; staff send and reply from FrontDesk."
      icon="mail"
      headerRight={
        summary ? (
          <Pill tone={summary.tone} dot={summary.tone !== 'neutral'}>
            {summary.label}
          </Pill>
        ) : null
      }
    >
      {isLoading ? (
        <div
          role="status"
          aria-label="Loading connected mailboxes"
          className="m-0 flex list-none flex-col overflow-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] p-0"
        >
          <MailboxRowSkeleton />
          <div className="border-t border-[var(--cream-2)]">
            <MailboxRowSkeleton />
          </div>
        </div>
      ) : accounts.length === 0 ? (
        <div className="flex flex-col items-start gap-[var(--sp-3)] rounded-[var(--r-lg)] border border-dashed border-[var(--cream-shadow)] bg-[var(--cream-0)] px-[var(--sp-5)] py-[var(--sp-5)]">
          <span
            aria-hidden
            className="inline-flex size-[34px] items-center justify-center rounded-[var(--r-md)] bg-[var(--surface-mute)] text-[var(--ink-2)]"
          >
            <Icon kind="mail" size={17} stroke={1.8} />
          </span>
          <div>
            <p className="m-0 text-sm font-semibold text-[var(--ink-0)]">No mailbox connected</p>
            <p className="mt-1.5 mb-0 max-w-[520px] text-[13px] leading-normal text-[var(--ink-2)]">
              Connect Gmail to send and receive with your clinic address (for example{' '}
              <span className="font-[var(--font-mono)] text-xs">elva.dental.nyc@gmail.com</span>){' '}
              straight from the FrontDesk inbox.
            </p>
          </div>
          <Button
            type="button"
            variant="primary"
            size="sm"
            iconLeft="link"
            loading={connecting}
            disabled={connecting || isLoading}
            onClick={onConnectGmail}
          >
            Connect Gmail
          </Button>
          <p className="m-0 text-[11px] text-[var(--ink-4)]">
            Opens Google to authorize — you can disconnect anytime.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-[var(--sp-4)]">
          <ul
            aria-label="Connected mailboxes"
            className="m-0 flex list-none flex-col overflow-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] p-0"
          >
            {accounts.map((account, index) => {
              const disconnecting = disconnectingId === account.id;
              const view = statusView(account.status);
              const canReconnect = needsReconnect(account.status);
              return (
                <li
                  key={account.id}
                  className="flex flex-wrap items-center gap-x-[var(--sp-4)] gap-y-[var(--sp-2)] px-[var(--sp-4)] py-3.5 transition-colors duration-[var(--dur-fast)] hover:bg-[var(--cream-0)]"
                  style={{ borderTop: index === 0 ? undefined : '1px solid var(--cream-2)' }}
                >
                  <div className="flex min-w-[200px] flex-1 items-center gap-[var(--sp-3)]">
                    <span
                      aria-hidden
                      className="inline-flex size-[34px] shrink-0 items-center justify-center rounded-[var(--r-md)] bg-[var(--grad-ink)] text-white"
                    >
                      <Icon kind="mail" size={16} stroke={1.8} />
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-[var(--ink-0)]">
                        {account.emailAddress}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-[var(--ink-3)]">
                        <span>{formatProvider(account.provider)}</span>
                        <span aria-hidden>·</span>
                        <span>Last sync {formatLastSync(account.lastSyncAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-auto flex shrink-0 items-center gap-[var(--sp-2)]">
                    <Pill tone={view.tone} dot>
                      {view.label}
                    </Pill>
                    {canReconnect ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        iconLeft="refresh"
                        loading={connecting}
                        disabled={connecting || disconnecting}
                        onClick={onConnectGmail}
                        aria-label={`Reconnect ${account.emailAddress}`}
                      >
                        Reconnect
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      loading={disconnecting}
                      disabled={disconnecting || connecting}
                      onClick={() => {
                        onDisconnect(account.id);
                      }}
                      aria-label={`Disconnect ${account.emailAddress}`}
                    >
                      Disconnect
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconLeft="plus"
              loading={connecting}
              disabled={connecting || isLoading}
              onClick={onConnectGmail}
            >
              Connect another mailbox
            </Button>
          </div>
        </div>
      )}
    </GeneralInfoSectionCard>
  );
}
